// src/components/setup/steps/DropshippingJoinRequest.jsx
import React, { useState, useEffect } from 'react';
import { Store, CheckCircle, ChevronDown } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import {
  getProviderByInviteHash,
  directJoinProvider,
  getBanks,
  getBankBranches,
  getDropshippingSettings,
} from '../../../services/api/dropshipping';
import styles from './StepComponents.module.css';

const DropshippingJoinRequest = ({ user, onNext, isSubmitting, setupProgress }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const inviteHash = user?.business?.setupProgress?.dropshippingProviderHash || setupProgress?.dropshippingProviderHash;

  const [providerInfo, setProviderInfo] = useState(null);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    requestedShopName: '',
    requestedPaymentCycle: '',
    deliveryCharge: '',
    resellerContactPhone: '',
    resellerAccountName: '',
    resellerAccountNumber: '',
    resellerBankId: null,
    resellerBankBranchId: null,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (form.resellerBankId) {
      getBankBranches(form.resellerBankId).then(setBranches).catch(() => setBranches([]));
    } else {
      setBranches([]);
      setForm((prev) => ({ ...prev, resellerBankBranchId: null }));
    }
  }, [form.resellerBankId]);

  const loadData = async () => {
    try {
      const [providerData, banksData, settings] = await Promise.all([
        inviteHash ? getProviderByInviteHash(inviteHash) : Promise.resolve(null),
        getBanks(),
        getDropshippingSettings(),
      ]);
      setProviderInfo(providerData);
      setBanks(banksData);
      if (providerData?.providerAllowedPaymentCycles?.length > 0) {
        setForm((prev) => ({
          ...prev,
          requestedPaymentCycle: providerData.providerAllowedPaymentCycles[0],
          resellerContactPhone: settings?.resellerContactPhone || '',
          resellerAccountName: settings?.resellerBankAccountName || '',
          resellerAccountNumber: settings?.resellerBankAccountNumber || '',
          resellerBankId: settings?.resellerBankId || null,
          resellerBankBranchId: settings?.resellerBankBranchId || null,
        }));
        if (settings?.resellerBankId) {
          const branchData = await getBankBranches(settings.resellerBankId);
          setBranches(branchData);
        }
      }
    } catch (err) {
      error(err.response?.data?.message || t('setup.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.requestedShopName.trim()) newErrors.requestedShopName = t('dropshipping.errorShopNameRequired');
    if (form.deliveryCharge === '' || form.deliveryCharge === null) newErrors.deliveryCharge = t('dropshipping.errorDeliveryChargeRequired');
    else if (parseFloat(form.deliveryCharge) < 0) newErrors.deliveryCharge = t('dropshipping.errorDeliveryChargeInvalid');
    if (!form.resellerContactPhone.trim()) newErrors.resellerContactPhone = t('dropshipping.errorContactPhoneRequired');
    if (!form.resellerAccountName.trim()) newErrors.resellerAccountName = t('dropshipping.errorAccountNameRequired');
    if (!form.resellerAccountNumber.trim()) newErrors.resellerAccountNumber = t('dropshipping.errorAccountNumberRequired');
    if (!form.resellerBankId) newErrors.resellerBankId = t('dropshipping.errorBankRequired');
    if (branches.length > 0 && !form.resellerBankBranchId) newErrors.resellerBankBranchId = t('dropshipping.errorBranchRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await directJoinProvider({
        inviteHash,
        requestedShopName: form.requestedShopName,
        requestedPaymentCycle: parseInt(form.requestedPaymentCycle),
        deliveryCharge: parseFloat(form.deliveryCharge),
        resellerContactPhone: form.resellerContactPhone,
        resellerAccountName: form.resellerAccountName,
        resellerAccountNumber: form.resellerAccountNumber,
        resellerBankId: form.resellerBankId,
        resellerBankBranchId: form.resellerBankBranchId,
      });
      success(t('setup.joinRequestSuccess', { providerName: providerInfo?.providerName || '' }));
      onNext();
    } catch (err) {
      error(err.response?.data?.message || t('setup.joinRequestError'));
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  if (loading) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>{t('setup.loadingProviderInfo')}</p>
        </div>
      </div>
    );
  }

  const allowedCycles = providerInfo?.providerAllowedPaymentCycles || [7];

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <Store className={styles.stepIcon} size={48} />
        <h2>{t('setup.dropshippingJoinTitle')}</h2>
        {providerInfo && (
          <p>{t('setup.dropshippingJoinDescription', { providerName: providerInfo.providerName })}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.stepForm}>
        {/* Shop name */}
        <div className={styles.formGroup}>
          <label htmlFor="shopName">{t('dropshipping.requestedShopName')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <input
            type="text"
            id="shopName"
            value={form.requestedShopName}
            onChange={(e) => setField('requestedShopName', e.target.value)}
            placeholder={t('dropshipping.requestedShopNamePlaceholder')}
            className={errors.requestedShopName ? styles.error : ''}
          />
          {errors.requestedShopName && <span className={styles.errorText}>{errors.requestedShopName}</span>}
        </div>

        {/* Payment cycle */}
        <div className={styles.formGroup}>
          <label htmlFor="paymentCycle">{t('dropshipping.requestedPaymentCycle')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <div className={styles.selectWrapper}>
            <select
              id="paymentCycle"
              className={styles.select}
              value={form.requestedPaymentCycle}
              onChange={(e) => setField('requestedPaymentCycle', e.target.value)}
            >
              {allowedCycles.map((c) => (
                <option key={c} value={c}>{c} {t('dropshipping.days')}</option>
              ))}
            </select>
            <ChevronDown className={styles.selectIcon} size={16} />
          </div>
        </div>

        {/* Delivery charge */}
        <div className={styles.formGroup}>
          <label htmlFor="deliveryCharge">{t('dropshipping.deliveryCharge')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <input
            type="number"
            id="deliveryCharge"
            min="0"
            step="0.01"
            value={form.deliveryCharge}
            onChange={(e) => setField('deliveryCharge', e.target.value)}
            className={errors.deliveryCharge ? styles.error : ''}
          />
          {errors.deliveryCharge
            ? <span className={styles.errorText}>{errors.deliveryCharge}</span>
            : <span className={styles.formHint}>{t('dropshipping.deliveryChargeHelp')}</span>}
        </div>

        {/* Contact phone */}
        <div className={styles.formGroup}>
          <label htmlFor="contactPhone">{t('dropshipping.resellerContactPhone')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <input
            type="text"
            id="contactPhone"
            value={form.resellerContactPhone}
            onChange={(e) => setField('resellerContactPhone', e.target.value)}
            placeholder={t('dropshipping.resellerContactPhonePlaceholder') || '+94 77 000 0000'}
            className={errors.resellerContactPhone ? styles.error : ''}
          />
          {errors.resellerContactPhone && <span className={styles.errorText}>{errors.resellerContactPhone}</span>}
        </div>

        {/* Bank */}
        <div className={styles.formGroup}>
          <label htmlFor="bank">{t('dropshipping.resellerBank')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <div className={styles.selectWrapper}>
            <select
              id="bank"
              className={`${styles.select} ${errors.resellerBankId ? styles.error : ''}`}
              value={form.resellerBankId || ''}
              onChange={(e) => {
                setField('resellerBankId', e.target.value ? parseInt(e.target.value) : null);
                setField('resellerBankBranchId', null);
              }}
            >
              <option value="">{t('dropshipping.selectBank')}</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <ChevronDown className={styles.selectIcon} size={16} />
          </div>
          {errors.resellerBankId && <span className={styles.errorText}>{errors.resellerBankId}</span>}
        </div>

        {/* Branch */}
        {branches.length > 0 && (
          <div className={styles.formGroup}>
            <label htmlFor="branch">{t('dropshipping.resellerBankBranch')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <div className={styles.selectWrapper}>
              <select
                id="branch"
                className={`${styles.select} ${errors.resellerBankBranchId ? styles.error : ''}`}
                value={form.resellerBankBranchId || ''}
                onChange={(e) => setField('resellerBankBranchId', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">{t('dropshipping.selectBranch') || 'Select Branch'}</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.branchName}</option>)}
              </select>
              <ChevronDown className={styles.selectIcon} size={16} />
            </div>
            {errors.resellerBankBranchId && <span className={styles.errorText}>{errors.resellerBankBranchId}</span>}
          </div>
        )}

        {/* Account name */}
        <div className={styles.formGroup}>
          <label htmlFor="accountName">{t('dropshipping.resellerAccountName')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <input
            type="text"
            id="accountName"
            value={form.resellerAccountName}
            onChange={(e) => setField('resellerAccountName', e.target.value)}
            className={errors.resellerAccountName ? styles.error : ''}
          />
          {errors.resellerAccountName && <span className={styles.errorText}>{errors.resellerAccountName}</span>}
        </div>

        {/* Account number */}
        <div className={styles.formGroup}>
          <label htmlFor="accountNumber">{t('dropshipping.resellerAccountNumber') || 'Account Number'} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <input
            type="text"
            id="accountNumber"
            value={form.resellerAccountNumber}
            onChange={(e) => setField('resellerAccountNumber', e.target.value)}
            className={errors.resellerAccountNumber ? styles.error : ''}
          />
          {errors.resellerAccountNumber && <span className={styles.errorText}>{errors.resellerAccountNumber}</span>}
        </div>

        <div className={styles.stepActions}>
          <button
            type="submit"
            disabled={submitting || isSubmitting}
            className={styles.primaryButton}
          >
            {submitting ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('setup.joiningProvider')}
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                {t('setup.submitJoinRequest')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DropshippingJoinRequest;
